import type { SxProps, Theme } from '@mui/material';
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { CurrencyDecimalInput, CurrencyFixedDecimalInput } from '@/shared/formatters';
import {
  RenterInviteAddressesAutocomplete,
  RenterInviteDatePicker,
  RenterInviteTextInput,
} from './components';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
}

export const LeaseInformationV2 = ({ control, errors, watch, setValue, trigger }: Props) => {
  const leaseStart = watch('leaseStart');

  return (
    <>
      <Typography variant="h6" component="h2" fontWeight="bold !important" lineHeight="28px" mb={1}>
        Lease information
      </Typography>
      <Typography
        variant="body2"
        fontSize="16px !important"
        fontWeight="350"
        mb={4}
        letterSpacing={0}
      >
        Enter the lease details below. If your renter is paying for the entire first month upfront
        at lease signing, we don’t need to cover it (optional).
      </Typography>
      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          return (
            <RenterInviteAddressesAutocomplete
              required
              size="small"
              value={field.value?.monolith_building_id}
              onBlur={() => field.onBlur()}
              inputSx={overrideStylesForAutocomplete}
              onChange={(value) => field.onChange(value)}
              name={field.name}
              label="Building Name or Address"
              showLoadingSkeleton={true}
              error={!!errors.address}
              helperText={
                errors.address?.monolith_building_id
                  ? errors.address?.monolith_building_id.message?.toString()
                  : errors.address?.message?.toString()
              }
            />
          );
        }}
      />
      <Controller
        name="apartmentNumber"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            id={'apartmentNumber'}
            label="Unit number"
            required
            error={!!errors.apartmentNumber}
            helperText={errors.apartmentNumber?.message?.toString()}
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: 4 }} mt={2}>
        <Controller
          name="leaseStart"
          control={control}
          render={({ field }) => (
            <RenterInviteDatePicker
              value={field.value ? dayjs(field.value) : null}
              label="Lease start date"
              maxDate={dayjs().add(1, 'year')}
              minDate={dayjs().subtract(1, 'year')}
              setValue={(value: dayjs.Dayjs) => {
                if (value?.isValid()) {
                  setValue('leaseStart', value.toDate(), { shouldValidate: true });
                  setValue('leaseEnd', value.add(1, 'year').subtract(1, 'day').toDate(), {
                    shouldValidate: true,
                  });
                }
              }}
              error={errors.leaseStart?.message?.toString()}
              setError={() => trigger('leaseStart')}
            />
          )}
        />
        <Controller
          name="leaseEnd"
          control={control}
          render={({ field }) => (
            <RenterInviteDatePicker
              value={field.value ? dayjs(field.value) : null}
              label="Lease end date"
              maxDate={leaseStart ? dayjs(leaseStart).add(10, 'year').add(1, 'month') : undefined}
              minDate={leaseStart ? dayjs(leaseStart).add(1, 'day') : undefined}
              setValue={(value: dayjs.Dayjs) => {
                if (value?.isValid()) {
                  setValue('leaseEnd', value.toDate());
                }
              }}
              error={errors.leaseEnd?.message?.toString()}
              setError={() => trigger('leaseEnd')}
            />
          )}
        />
      </Stack>
      <Controller
        name="grossMonthlyRent"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            InputProps={{
              inputComponent: CurrencyDecimalInput,
              disableUnderline: true,
            }}
            label="Total monthly rent"
            id={'gross_monthly_rent'}
            required
            error={!!errors.grossMonthlyRent}
            helperText={
              errors.grossMonthlyRent?.message?.toString() ??
              'This is the market price rent that is on the lease.'
            }
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: 4 }}>
        <Controller
          name="prepaidRent"
          control={control}
          render={({ field }) => (
            <RenterInviteTextInput
              {...field}
              ref={null}
              size="small"
              InputProps={{
                inputComponent: CurrencyFixedDecimalInput,
                disableUnderline: true,
              }}
              label="Prepaid rent"
              id={'prepaid-rent'}
              error={!!errors.prepaidRent}
              helperText={
                <>
                  <Box>{errors.prepaidRent?.message?.toString()}</Box>
                  <Typography variant="helpertext">
                    Any rent that will be collected at lease signing. Prepaid rent is not covered by
                    the policy.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
        <Controller
          name="freeRent"
          control={control}
          render={({ field }) => (
            <RenterInviteTextInput
              {...field}
              ref={null}
              size="small"
              InputProps={{
                inputComponent: CurrencyFixedDecimalInput,
                disableUnderline: true,
              }}
              label="Free rent"
              id={'free-rent'}
              error={!!errors.freeRent}
              helperText={
                <>
                  <Box>{errors.freeRent?.message?.toString()}</Box>
                  <Typography variant="helpertext">
                    Any concessions offered to the renter where they are not responsible for making
                    rent payments.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
      </Stack>
    </>
  );
};

/**
 * These styles are used to match the old styles from the invite form,
 * because it doesn't use the component library. To get rid of this,
 * we need to migrate to the component library entirely
 */
const overrideStylesForAutocomplete: SxProps<Theme> = {
  '& .MuiFormLabel-root.Mui-error': {
    color: 'rgb(255, 48, 48) !important',
  },
  '& .MuiInputBase-root.Mui-error': {
    boxShadow: 'rgb(255, 48, 48) 0px 0px 0px 1px inset',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: 'rgb(255, 48, 48) !important',
  },
};
